import React from 'react';
import { Box, List, ListItem, ListItemIcon, ListItemText, Avatar, Typography, Divider, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useBranding } from '../../contexts/ThemeContext';
import apiClient from '../../services/api/config';
import {
  Home,
  School,
  SportsEsports,
  Campaign,
  Policy,
  CalendarMonth,
  FolderOpen,
  EmojiEvents,
  Store,
  Forum,
  Assignment,
  AdminPanelSettings,
  Close,
  AutoStories,
  Spa,
  Chat,
  LiveTv,
  Logout,
  Settings,
  Person,
  Diversity3,
} from '@mui/icons-material';

const NavList = styled(List)({
  flex: 1,
  overflowY: 'auto',
  padding: '8px',
});

interface NavItemData {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  moduleName?: string;
  adminOnly?: boolean;
}

const navItems: NavItemData[] = [
  { id: 'dashboard', label: 'Dashboard', icon: <Home />, path: '/dashboard' },
  { id: 'announcements', label: 'Announcements', icon: <Campaign />, path: '/announcements', moduleName: 'announcements' },
  { id: 'policies', label: 'Policies', icon: <Policy />, path: '/policies', moduleName: 'policies' },
  { id: 'culture', label: 'Culture Hub', icon: <Diversity3 />, path: '/culture', moduleName: 'cultureHub' },
  { id: 'learning', label: 'Learning', icon: <School />, path: '/learning', moduleName: 'learning' },
  { id: 'microlearning', label: 'Microlearning', icon: <AutoStories />, path: '/microlearning', moduleName: 'microlearning' },
  { id: 'games', label: 'Games', icon: <SportsEsports />, path: '/gamification', moduleName: 'games' },
  { id: 'wellness', label: 'Wellness', icon: <Spa />, path: '/wellness', moduleName: 'wellness' },
  { id: 'rewards', label: 'Rewards', icon: <EmojiEvents />, path: '/rewards', moduleName: 'rewards' },
  { id: 'store', label: 'Store', icon: <Store />, path: '/store', moduleName: 'rewards' },
  { id: 'pulse', label: 'Pulse', icon: <Forum />, path: '/pulse', moduleName: 'chat' },
  { id: 'tasks', label: 'Tasks', icon: <Assignment />, path: '/projects', moduleName: 'projects' },
  { id: 'calendar', label: 'Calendar', icon: <CalendarMonth />, path: '/calendar', moduleName: 'calendar' },
  { id: 'documents', label: 'Documents', icon: <FolderOpen />, path: '/documents', moduleName: 'policies' },
  { id: 'chat', label: 'Chat', icon: <Chat />, path: '/chat', moduleName: 'chat' },
  { id: 'streaming', label: 'Live Stream', icon: <LiveTv />, path: '/streaming', moduleName: 'livestream' },
];

interface MobileNavigationProps {
  onClose: () => void;
}

function MobileNavigation({ onClose }: MobileNavigationProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { branding } = useBranding();
  const [enabledModules, setEnabledModules] = React.useState<string[]>([]);

  React.useEffect(() => {
    const fetchModules = async () => {
      try {
        const response = await apiClient.get('/api/company/modules');
        if (response.data?.success && response.data?.data) {
          const activeModules = response.data.data
            .filter((m: { isActive: boolean }) => m.isActive)
            .map((m: { moduleName: string }) => m.moduleName);
          setEnabledModules(activeModules);
        }
      } catch (error) {
        setEnabledModules(['wellness', 'learning', 'microlearning', 'games', 'announcements', 'chat', 'projects', 'rewards', 'policies', 'analytics', 'livestream', 'calendar', 'documents', 'cultureHub', 'pulse', 'badges', 'askAria']);
      }
    };
    fetchModules();
  }, []);

  const isModuleEnabled = (moduleName?: string) => {
    if (!moduleName) return true;
    return enabledModules.includes(moduleName);
  };

  const sidebarBg = branding.sidebarColor || '#1A1D1F';
  const sidebarText = branding.sidebarTextColor || '#FFFFFF';
  const primaryColor = branding.primaryColor || '#C0EB38';

  const resolveUrl = (url: string) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/r2/')) return `${apiClient.defaults.baseURL || ''}${url}`;
    return url;
  };

  const isAdmin = user && ['admin', 'company-super-admin', 'super-super-admin'].includes(user.role);
  const isSuperSuperAdmin = user?.role === 'super-super-admin';

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleNavClick = (path: string) => {
    navigate(path);
    onClose();
  };

  const navItemSx = (active: boolean) => ({
    borderRadius: '12px',
    marginBottom: '4px',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation',
    backgroundColor: active ? `${primaryColor}25` : 'transparent',
    '& .MuiListItemIcon-root': { color: active ? primaryColor : `${sidebarText}B3`, minWidth: '40px' },
    '& .MuiListItemText-primary': { color: active ? primaryColor : `${sidebarText}E6`, fontWeight: active ? 600 : 400 },
  });

  return (
    <Box sx={{
      width: 280,
      height: '100%',
      background: sidebarBg,
      color: sidebarText,
      display: 'flex',
      flexDirection: 'column',
    }}>
      <Box sx={{
        padding: '20px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        borderBottom: `1px solid ${sidebarText}1A`,
      }}>
        {branding.logoUrl ? (
          <Box
            component="img"
            src={resolveUrl(branding.logoUrl)}
            alt={branding.companyName || 'Logo'}
            sx={{ width: 32, height: 32, objectFit: 'contain' }}
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL}/brand/logo-mark.svg`}
            alt="MoreMe AI"
            sx={{ width: 32, height: 32, filter: 'brightness(10)' }}
          />
        )}
        <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>
          {branding.companyName || 'MoreMe AI'}
        </Typography>
        <Close 
          onClick={onClose} 
          sx={{ cursor: 'pointer', color: `${sidebarText}B3`, '&:hover': { color: sidebarText } }} 
        />
      </Box>

      <NavList>
        {!isSuperSuperAdmin && navItems.filter(item => isModuleEnabled(item.moduleName)).map((item) => (
          <ListItem
            key={item.id}
            disablePadding
            sx={navItemSx(isActive(item.path))}
            onTouchEnd={(e) => { e.preventDefault(); handleNavClick(item.path); }}
            onClick={() => handleNavClick(item.path)}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItem>
        ))}

        {isAdmin && (
          <>
            {!isSuperSuperAdmin && <Divider sx={{ my: 2, borderColor: `${sidebarText}1A` }} />}
            <ListItem
              sx={navItemSx(isSuperSuperAdmin ? isActive('/admin/super') : isActive('/admin'))}
              onClick={() => handleNavClick(isSuperSuperAdmin ? '/admin/super' : '/admin')}
            >
              <ListItemIcon><AdminPanelSettings /></ListItemIcon>
              <ListItemText primary={isSuperSuperAdmin ? 'Super Admin Dashboard' : 'Admin Hub'} />
            </ListItem>
          </>
        )}

        {!isSuperSuperAdmin && (
        <>
        <Divider sx={{ my: 2, borderColor: `${sidebarText}1A` }} />
        <ListItem
          sx={navItemSx(isActive('/profile'))}
          onClick={() => handleNavClick('/profile')}
        >
          <ListItemIcon><Person /></ListItemIcon>
          <ListItemText primary="Profile" />
        </ListItem>
        <ListItem
          sx={navItemSx(isActive('/settings'))}
          onClick={() => handleNavClick('/settings')}
        >
          <ListItemIcon><Settings /></ListItemIcon>
          <ListItemText primary="Settings" />
        </ListItem>
        </>
        )}
      </NavList>

      <Box sx={{ padding: '16px', borderTop: `1px solid ${sidebarText}1A` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar
            src={user?.avatar}
            sx={{
              width: 44,
              height: 44,
              background: primaryColor,
              color: sidebarBg,
            }}
          >
            {user?.firstName?.[0]}{user?.lastName?.[0]}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {user?.firstName} {user?.lastName}
            </Typography>
            <Typography variant="caption" sx={{ color: `${sidebarText}99` }}>
              {user?.role}
            </Typography>
          </Box>
        </Box>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<Logout />}
          onClick={() => { onClose(); logout(); }}
          sx={{
            color: '#EF4444',
            borderColor: 'rgba(239,68,68,0.3)',
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': {
              borderColor: '#EF4444',
              bgcolor: 'rgba(239,68,68,0.1)',
            },
          }}
        >
          Sign Out
        </Button>
      </Box>
    </Box>
  );
}

export default MobileNavigation;
